/*
  Stoffeya Merkzettel (wishlist) without an app.
  Saved products live in this browser's localStorage by product handle, so it
  works for guests and costs nothing; it does not follow the shopper to
  another device.

  <stoffeya-merkzettel-button data-handle>  heart on cards and product page
  <stoffeya-merkzettel-count>               number in the header icon
  <stoffeya-merkzettel-page>                the list on /pages/merkzettel;
    each card is rendered by Shopify via
    /products/<handle>?section_id=stoffeya-merkzettel-card
*/
if (!customElements.get('stoffeya-merkzettel-button')) {
  const STORE_KEY = 'stoffeya-merkzettel';
  const CHANGE_EVENT = 'stoffeya:merkzettel';
  const MAX_ITEMS = 100;

  const readList = () => {
    try {
      const list = JSON.parse(localStorage.getItem(STORE_KEY));
      return Array.isArray(list) ? list.filter((item) => item && typeof item.handle === 'string') : [];
    } catch (error) {
      return [];
    }
  };

  const announce = () => document.dispatchEvent(new CustomEvent(CHANGE_EVENT));

  const writeList = (list) => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
    } catch (error) {
      // Storage blocked (private mode): the heart simply does not stick
    }
    announce();
  };

  const isSaved = (handle) => readList().some((item) => item.handle === handle);

  // Returns true when the product was added, false when removed
  const toggle = (handle) => {
    const list = readList();
    const index = list.findIndex((item) => item.handle === handle);
    if (index >= 0) {
      list.splice(index, 1);
    } else {
      list.unshift({ handle, addedAt: Date.now() });
    }
    writeList(list);
    return index < 0;
  };

  // Keep other open tabs in step
  window.addEventListener('storage', (event) => {
    if (event.key === STORE_KEY) announce();
  });

  // Back button on phones restores the previous page from memory (bfcache):
  // hearts tapped meanwhile on the product page must show there too
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) announce();
  });

  class MerkzettelButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', this.onClick);
      document.addEventListener(CHANGE_EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener(CHANGE_EVENT, this.render);
    }

    onClick = (event) => {
      // The heart sits on top of the card link
      event.preventDefault();
      event.stopPropagation();
      const added = toggle(this.dataset.handle);
      window.posthog?.capture?.(added ? 'merkzettel_add' : 'merkzettel_remove', { handle: this.dataset.handle });
    };

    render = () => {
      const saved = isSaved(this.dataset.handle);
      this.classList.toggle('is-saved', saved);
      this.button?.setAttribute('aria-pressed', String(saved));
    };
  }

  class MerkzettelCount extends HTMLElement {
    connectedCallback() {
      document.addEventListener(CHANGE_EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.render);
    }

    render = () => {
      const count = readList().length;
      const bubble = this.querySelector('.merkzettel-count');
      if (!bubble) return;
      bubble.hidden = count === 0;
      bubble.querySelector('[aria-hidden]').textContent = count < 100 ? count : '99+';
      bubble.querySelector('.visually-hidden').textContent = (this.dataset.labelCount || '').replace('[count]', count);
    };
  }

  class MerkzettelPage extends HTMLElement {
    connectedCallback() {
      document.addEventListener(CHANGE_EVENT, this.onChange);
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
    }

    get grid() {
      return this.querySelector('.merkzettel__grid');
    }

    // A heart tapped on this page removes its card straight away
    onChange = () => {
      const handles = new Set(readList().map((item) => item.handle));
      this.grid.querySelectorAll('[data-merkzettel-handle]').forEach((item) => {
        if (!handles.has(item.dataset.merkzettelHandle)) item.remove();
      });
      this.updateState();
    };

    async load() {
      const list = readList();
      this.updateState();
      if (list.length === 0) return;

      this.classList.add('is-loading');
      const root = window.Shopify?.routes?.root || '/';
      const results = await Promise.all(
        list.map(async ({ handle }) => {
          try {
            const response = await fetch(
              `${root}products/${encodeURIComponent(handle)}?section_id=stoffeya-merkzettel-card`
            );
            if (response.status === 404) return { handle, gone: true };
            if (!response.ok) return { handle };
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const item = doc.querySelector('.grid__item');
            // An unknown handle can come back 200 with an empty section
            return item ? { handle, item } : { handle, gone: true };
          } catch (error) {
            return { handle };
          }
        })
      );

      // Deleted or unpublished products leave the list, or the header count never matches
      const gone = new Set(results.filter((result) => result.gone).map((result) => result.handle));
      if (gone.size) writeList(readList().filter((item) => !gone.has(item.handle)));

      this.grid.replaceChildren(
        ...results
          .filter((result) => result.item)
          .map((result) => {
            const item = document.adoptNode(result.item);
            item.dataset.merkzettelHandle = result.handle;
            return item;
          })
      );
      this.classList.remove('is-loading');
      this.updateState();
      if (typeof updateShowMoreButtons === 'function') updateShowMoreButtons();
    }

    updateState() {
      const count = readList().length;
      this.querySelector('.merkzettel__empty').hidden = count > 0;
      this.grid.hidden = count === 0;
    }
  }

  customElements.define('stoffeya-merkzettel-button', MerkzettelButton);
  customElements.define('stoffeya-merkzettel-count', MerkzettelCount);
  customElements.define('stoffeya-merkzettel-page', MerkzettelPage);
}
